// The queue view — route /tickets.
//
// GET /api/tickets (docs/API.md §3.1) is the highest-traffic read in the app: one
// paginated `{ data, page }` request per filter change, with limit/offset paging
// because the queue shows a real total ("62 unsolved") and supports jumping pages.
//
// Filter state lives in the URL rather than in component state, so a filtered queue is
// a link an agent can send, and Back steps through filter changes.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { api } from '../../api/client.js';
import { useLiveRefresh } from '../../hooks/useLiveRefresh.js';
import TicketFilters from '../../components/tickets/TicketFilters.jsx';
import ViewPicker from '../../components/tickets/ViewPicker.jsx';
import { StatusBadge, PriorityLabel, ChannelLabel } from '../../components/tickets/Badges.jsx';
import { formatRelative, formatDateTime } from '../../components/tickets/format.js';
import { visibleColumns } from '../../components/tickets/ticket-columns.js';
import { useAppSettings } from '../../hooks/useAppSettings.js';
import { slaCell } from '../../components/tickets/sla-state.js';
import {
  PAGE_SIZE,
  readFilters,
  toApiQuery,
  toSearchParams,
} from '../../components/tickets/queue-query.js';
import { SORTS } from '../../components/tickets/TicketFilters.jsx';
import {
  readSortPreference, writeSortPreference,
} from '../../components/tickets/sort-preference.js';

// The queue's column strategy, in one place.
//
// `table-fixed` plus this colgroup is what stops the horizontal scrollbar. Under
// `table-layout: auto` the table's width is max(container, sum of per-column
// min-content) — and six of the nine cells were `whitespace-nowrap`, so their
// min-content was their full single-line text. The sum exceeded the ~968px the
// layout allows (max-w-7xl 1280 - px-8 64 - sidebar 224 - gap 24) and the card
// scrolled sideways. Worse, UPDATED is last, and at the time it was the column the
// queue was SORTED by, so the first thing hidden was the thing the order was based on.
// (The default is `-created_at` now, but the column-width reasoning below stands.)
//
// Note `max-w-md` used to sit on the subject cell and did nothing at all:
// `max-width` on a table-cell box is undefined in CSS 2.1 §10.4. Widths belong
// here, on the <col>, which is where the fixed algorithm reads them.
//
// Fixed px for the columns whose content has a known ceiling; percentages for the
// four that carry free text and therefore truncate.
//
// TEN columns now, not nine. The SLA column is FIXED at 7rem: "Reply due" needs
// 96.1px of the 112px in the real `th` styling, and the widest state word
// ("Overdue") needs less. It is fixed rather than a percentage because the
// invariant that stops the horizontal scrollbar is `sum of fixed px < the
// NARROWEST table box` — 624px against the 710px MEASURED at viewport 1024, where
// the sidebar and lg:px-8 first apply and the queue is at its tightest. That is
// also why the column is not the 8.75rem a phrase like "Breached 3d ago" would
// need: the column says a STATE, never a countdown.
//
// Position is free — under table-fixed the rendered widths depend on the multiset
// of declared widths, not their order. It sits after SUBJECT because the breach
// chip used to live inside the subject cell, so agents already look there, and
// because it keeps the red "Overdue" chip three columns clear of `prio-urgent`,
// whose red-600 text sits in the PRIORITY column meaning something else.
//
// A HEADER MUST NEVER ELLIPSIZE. A body cell truncating is the point of a fixed
// table — a long subject should get an ellipsis and keep its `title`. A column
// whose NAME is unreadable is just a broken column. So every fixed width below is
// at least the header's own rendered width plus the 32px its `px-4` costs.
// MEASURED in headless Chrome against the built stylesheet, at the real
// text-xs/font-medium/uppercase/tracking-wide styling — the required widths are
// REF 53.4, REPLY DUE 96.1, STATUS 76.8, PRIORITY 88.7, CHANNEL 90.7, UPDATED
// 88.7. PRIORITY and CHANNEL were `5.5rem` (88px) and so clipped by 0.7px and
// 2.7px — which is exactly why they showed as `PRIORI…` and `CHAN…` rather than
// as obvious overflow. Both are now 7rem, which also makes the state-word columns
// a uniform set. The tightest remaining header margin is CHANNEL's 21.3px.
//
// UPDATED is 9rem for a DIFFERENT reason, and it is the one that puts a horizontal
// scrollbar back if you get it wrong. Its `td` is `whitespace-nowrap` with NO
// `overflow-hidden`, so its text does not truncate — it SPILLS, and it is the last
// column, so the spill lands outside the table and `overflow-x-auto` starts
// scrolling. MEASURED: the widest string `formatRelative` can return is
// "59 minutes ago" at 127.6px including padding, and at the old 6.5rem (104px) that
// overflowed by 23.6px — card scrollWidth 1614 inside clientWidth 1606, with the
// only overflowing cell in column 9. 9rem = 144px clears it by 16.4px. THE FIXED
// WIDTHS ARE NOT THE CAUSE OF THAT SCROLLBAR: under `table-layout: fixed` Chrome
// renormalizes over-subscribed percentage columns rather than overflowing (the
// array asks for 664px + 69% of a 1606px box = 105% and still renders to exactly
// 1606px, MEASURED at 1920/1280/1024/768), so the table only exceeds its box when
// the sum of the FIXED widths alone does — 664px, which happens below a ~712px
// table box, i.e. a viewport under about 720 in the stacked layout.
//
// The FOUR percentage columns cannot carry the header guarantee, and it is worth
// being precise about why rather than discovering it later: a percentage is a ratio
// of the leftover, so its rendered width falls with the viewport, and forcing a
// minimum on it WOULD push the table past the card. MEASURED, their headers start
// to ellipsize below viewport 1612 (ORGANIZATION), 1420 (ASSIGNEE), 1339
// (CUSTOMER) and 1160 (SUBJECT). All ten fit on the 1920-wide monitor this queue is
// used on, which is the width this split is tuned for.
/**
 * One renderer per column key, so the body cells stop being POSITIONAL.
 *
 * They were hand-written in order while the colgroup, the headers and EmptyRow's
 * colSpan all mapped over the catalogue — and this file's own comment recorded what
 * that costs: "under table-fixed a cell inserted at the wrong index does not error or
 * reflow: every cell after it silently renders under the wrong header at the wrong
 * width." Keying them removes that entirely, and is also what lets a view choose which
 * columns appear: rendering a subset in a different order is now a `map`.
 *
 * Every cell's markup is unchanged from the positional version, comments included.
 * The `key` prop is on the <td> because these are rendered from an array now.
 */
const CELLS = {
  ref: (ticket) => (
    <td key="ref" className="whitespace-nowrap px-4 py-2 font-mono text-xs text-ink-muted group-hover:text-ink-secondary">
      {/* brand-700 is the readable link hover on white and near-invisible
          on a dark card, so this is a real palette direction change. */}
      <Link
        to={`/tickets/${ticket.public_ref}`}
        className="hover:text-brand-700 dark:hover:text-brand-300"
      >
        #{ticket.public_ref}
      </Link>
    </td>
  ),

  subject: (ticket) => (
    <td key="subject" className="px-4 py-2">
      <Link
        to={`/tickets/${ticket.public_ref}`}
        title={ticket.subject}
        className="block truncate font-medium text-ink hover:text-brand-700 dark:hover:text-brand-300"
      >
        {ticket.subject}
      </Link>
      {/* Rendered only when it has something in it. This container was
          unconditional, so every untagged ticket without an SLA flag — most
          of the queue — paid `mt-1` plus an empty flex box in row height.

          The condition used to also test the two SLA fields, which was a
          latent version of that same bug rather than a fix for it: SlaFlag
          returns null unless `breached`, so the moment any clock started,
          every unbreached untagged ticket would render an empty flex box
          again (+4px of row height, zero children). SLA has its own column
          now, so the condition collapses to the one thing that actually
          puts a child in here — which deletes the bug instead of patching
          it. */}
      {ticket.tags.length > 0 && (
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {ticket.tags.map((tag) => (
            // ring-inset so the chip keeps its shape on a hovered row.
            // `bg-sunken` is byte-identical to the row's own hover fill
            // (--color-sunken is slate-100), so without a ring these chips
            // dissolve into the row exactly when the pointer is on them.
            <span
              key={tag}
              className="rounded bg-sunken px-1.5 py-0.5 text-xs text-ink-secondary
                         ring-1 ring-inset ring-line-strong/60"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </td>
  ),

  // No arithmetic here and no Date.now(): `sla_state` was decided by the database
  // server on the same statement as `sla_breached`, so the two cannot disagree
  // (web/src/components/tickets/sla-state.js).
  sla: (ticket) => <SlaCell key="sla" ticket={ticket} />,

  // One line, not two. This cell was unconditionally two lines and so it,
  // alone, set every row to ~60px — the row height was being decided by a
  // column nobody triages on. The email is still recoverable on hover and
  // is still what the row is identified by in the detail view.
  customer: (ticket) => (
    <td key="customer" className="px-4 py-2 text-ink" title={ticket.customer.email}>
      <div className="truncate">{ticket.customer.name}</div>
    </td>
  ),

  organization: (ticket) => (
    <td key="organization" className="truncate px-4 py-2 text-ink-secondary" title={ticket.organization?.name ?? undefined}>
      {ticket.organization?.name ?? (
        <span className="text-ink-muted group-hover:text-ink-secondary">-</span>
      )}
    </td>
  ),

  status: (ticket) => (
    <td key="status" className="whitespace-nowrap px-4 py-2">
      <StatusBadge status={ticket.status} namedStatus={ticket.named_status} />
    </td>
  ),

  priority: (ticket) => (
    <td key="priority" className="whitespace-nowrap px-4 py-2">
      <PriorityLabel priority={ticket.priority} />
    </td>
  ),

  assignee: (ticket) => (
    <td key="assignee" className="truncate px-4 py-2 text-ink-secondary" title={ticket.assignee?.display_name ?? undefined}>
      {ticket.assignee?.display_name ?? (
        <span className="text-ink-muted group-hover:text-ink-secondary">Unassigned</span>
      )}
    </td>
  ),

  channel: (ticket) => (
    <td key="channel" className="whitespace-nowrap px-4 py-2">
      <ChannelLabel channel={ticket.channel} />
    </td>
  ),

  updated: (ticket) => (
    <td
      key="updated"
      className="whitespace-nowrap px-4 py-2 text-ink-muted group-hover:text-ink-secondary"
      title={formatDateTime(ticket.updated_at) ?? undefined}
    >
      {formatRelative(ticket.updated_at)}
    </td>
  ),
};

function EmptyRow({ span, children }) {
  return (
    <tr>
      {/* Derived, not counted. The literal 9 here had to be kept in step with the
          header list by hand, which is the kind of duplication that survives right
          up until someone adds a column. */}
      <td colSpan={span} className="px-4 py-12 text-center text-sm text-ink-muted">
        {children}
      </td>
    </tr>
  );
}

/**
 * The SLA cell. A `<td>` rather than its contents, so the whole cell carries the
 * title and the cell's own padding is not duplicated at two call sites.
 *
 * Every decision here was made on the server: `sla_state` is computed in the same
 * SQL statement, against the same `SYSUTCDATETIME()`, that decides `sla_breached`,
 * so the two can never disagree the way a workstation-clock countdown could.
 */
function SlaCell({ ticket }) {
  const cell = slaCell(ticket);
  return (
    <td className="whitespace-nowrap px-4 py-2" title={cell.title}>
      {cell.label === null ? (
        // "—" announces as nothing useful, so the meaning goes in sr-only text.
        // Colour is never the only carrier in this column, and neither is `title`:
        // it is not keyboard reachable, is unreliably announced, and never appears
        // on touch.
        <>
          <span aria-hidden="true" className={cell.className}>-</span>
          <span className="sr-only">{cell.srLabel}</span>
        </>
      ) : (
        <span className={cell.className}>{cell.label}</span>
      )}
    </td>
  );
}

export default function TicketList() {
  const settings = useAppSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  const [result, setResult] = useState({ status: 'loading', tickets: [], page: null, error: null });
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [views, setViews] = useState({ status: 'loading', list: [] });
  const [reloadToken, setReloadToken] = useState(0);

  // The agent's remembered order, read once and validated against the list the picker
  // actually offers (SORTS) -- a value stored by an older build would otherwise be sent
  // to the API, which answers an unknown `sort` with 400 and breaks the whole queue.
  // `effectiveSort` inside toApiQuery is what stops it reaching a request while a view
  // is open, since a view carries its own order.
  const [preferredSort, setPreferredSort] = useState(
    () => readSortPreference(SORTS.map((option) => option.value)),
  );
  const query = toApiQuery(filters, preferredSort);

  /**
   * Fetch the queue. `quiet` is the difference between an agent asking and a timer.
   *
   * A visible load announces itself and owns the error state: the agent changed a
   * filter, and if that fails they need to be told rather than left reading the
   * previous filter's results. A quiet one does neither. Showing `loading` on a poll
   * would flicker the whole table every twenty seconds, and taking the error branch
   * would empty a working queue because one background request lost a race with a
   * deploy or a dropped connection — the next poll is twenty seconds away and will
   * almost certainly succeed.
   */
  const fetchTickets = useCallback(
    (signal, { quiet = false } = {}) => {
      if (!quiet) setResult((previous) => ({ ...previous, status: 'loading', error: null }));
      return api
        .get(`/tickets?${query}`, { signal })
        .then((payload) => {
          setResult({
            status: 'ready',
            tickets: payload?.data ?? [],
            page: payload?.page ?? null,
            error: null,
          });
        })
        .catch((error) => {
          if (signal?.aborted || error.name === 'AbortError') return;
          if (quiet) return;
          setResult({ status: 'error', tickets: [], page: null, error });
        });
    },
    [query],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchTickets(controller.signal);
    return () => controller.abort();
  }, [fetchTickets, reloadToken]);

  // A ticket that arrives by email shows up here on its own. Without this an agent
  // could not tell an empty queue from a stale page, which is the difference between
  // "nothing came in" and "you have missed four minutes of work".
  useLiveRefresh(() => fetchTickets(undefined, { quiet: true }), {
    enabled: result.status === 'ready',
  });

  // The assignee picker is a convenience, not a gate: if this call fails the queue
  // still works with Anyone / Me / Unassigned (docs/API.md §5.5 — filtering the list
  // is presentation, the server does the authorization).
  useEffect(() => {
    const controller = new AbortController();
    api.get('/users?can_be_assigned=true', { signal: controller.signal })
      .then((payload) => setAssignableUsers(payload?.data ?? []))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  // Views are fetched once, not per filter change: the list is small, and it does not
  // depend on anything in the query. Like the assignee picker, a failure here is not
  // fatal — the queue works without views, so the sidebar says so and gets out of the
  // way (docs/API.md §3.6).
  useEffect(() => {
    const controller = new AbortController();
    api.get('/views', { signal: controller.signal })
      .then((payload) => setViews({ status: 'ready', list: payload?.data ?? [] }))
      .catch((error) => {
        if (controller.signal.aborted || error.name === 'AbortError') return;
        setViews({ status: 'error', list: [] });
      });
    return () => controller.abort();
  }, []);

  const updateFilters = useCallback(
    (patch) => {
      // Any filter change resets paging: page 3 of the old result set is meaningless
      // against the new one. Only an explicit page step carries an offset through.
      const offset = 'offset' in patch ? patch.offset : 0;
      setSearchParams(toSearchParams({ ...filters, ...patch, offset }));
    },
    [filters, setSearchParams],
  );

  const page = result.page;
  const showingFrom = page && page.total > 0 ? page.offset + 1 : 0;
  const showingTo = page ? page.offset + result.tickets.length : 0;

  const activeView = views.list.find((view) => view.id === filters.view) ?? null;

  // An unknown or no-longer-visible view id is IGNORED by the API rather than 404ing
  // (docs/API.md §3.1), so once the list has loaded and nothing matches, the sidebar
  // highlights "All tickets" — which is what the server actually applied. Before the
  // list loads there is nothing to match against, so the URL is taken at its word.
  const activeId = activeView ? activeView.id : views.status === 'ready' ? '' : filters.view;

  // The columns this view asks for, resolved against the catalogue. A view with no
  // arrangement saved — which is every view until an admin makes one — falls back to
  // the full catalogue, so the queue an agent already knows is what they keep getting.
  // Memoised because it is the dependency of a render that maps over it per row.
  // A VIEW'S OWN ARRANGEMENT WINS; the setting is what applies when no view is
  // selected, which used to be a list compiled into this bundle. Chained rather than
  // branched: `visibleColumns` already treats an empty arrangement as "no arrangement",
  // so an unset setting falls through to the catalogue exactly as it did before.
  const columns = useMemo(
    () => visibleColumns(activeView?.columns?.length ? activeView.columns : settings['tickets.default_columns']),
    [activeView, settings],
  );

  return (
    // items-start keeps the sidebar its own height instead of stretching it to match
    // a long queue. min-w-0 on the queue column is what lets the table's own
    // overflow-x-auto do its job — without it a wide table widens the flex item
    // instead of scrolling, and pushes the page into a horizontal scroll.
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <aside className="lg:w-56 lg:shrink-0">
        <ViewPicker
          views={views.list}
          status={views.status}
          activeId={activeId}
          onSelect={(id) => updateFilters({ view: id })}
        />
      </aside>

      <div className="min-w-0 flex-1 space-y-4">
      <div className="flex items-center justify-between">
        {/* The heading names the view rather than always saying "Tickets", so the
            answer to "which queue am I looking at?" does not depend on spotting the
            highlight in the sidebar. */}
        <h1 className="text-xl font-semibold tracking-tight">
          {activeView ? activeView.name : 'Tickets'}
        </h1>
        {page && (
          <p className="text-sm text-ink-muted">
            {page.total.toLocaleString()} {page.total === 1 ? 'ticket' : 'tickets'} match
          </p>
        )}
      </div>

      <TicketFilters
        filters={filters}
        onChange={updateFilters}
        assignableUsers={assignableUsers}
        viewActive={activeId !== ''}
        preferredSort={preferredSort}
        onSaveDefault={(value) => { writeSortPreference(value); setPreferredSort(value); }}
      />

      {result.status === 'error' && (
        // No semantic "error surface" token exists, and this is one of the two places
        // (with New.jsx) that needs one, so the red steps are spelled out with an
        // explicit dark pair: a 50 fill / 200 border / 800 text card inverts to a 950
        // fill with a 900 border and 200/300 text (11.1:1 and 8.5:1), which no single
        // token can express. See notes — worth a token if a third one appears.
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm font-medium text-red-800 dark:text-red-200">Could not load the queue.</p>
          <p className="mt-1 text-sm text-red-700 dark:text-red-300">{result.error?.message}</p>
          <button
            type="button"
            className="btn-ghost mt-3 ring-1 ring-red-300 dark:ring-red-800"
            onClick={() => setReloadToken((token) => token + 1)}
          >
            Try again
          </button>
        </div>
      )}

      {/* The queue is the surface agents stare at all day, so its structure comes
          from tokens rather than from borrowed slate steps: the table body is a
          raised surface, the header row is inset into it, and every rule is
          border-line — which is a hairline on white and a lifted #303a4b on dark,
          instead of the harsh near-white lines a borrowed slate-200 would draw.
          Rows are separated by rules rather than zebra fills on purpose; striping a
          dark table with a third grey collapses the surface/sunken steps that the
          header and the hover state depend on. */}
      <div className="overflow-x-auto rounded-lg border border-line bg-surface">
        <table className="w-full table-fixed divide-y divide-line text-sm">
          <colgroup>
            {columns.map((col) => (
              <col key={col.key} className={col.width} />
            ))}
          </colgroup>
          <thead className="bg-sunken text-left text-xs font-medium uppercase tracking-wide text-ink-secondary">
            <tr>
              {columns.map((col) => (
                <th key={col.key} scope="col" className="truncate px-4 py-2.5">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {result.status === 'loading' && result.tickets.length === 0 && (
              <EmptyRow span={columns.length}>Loading…</EmptyRow>
            )}

            {result.status === 'ready' && result.tickets.length === 0 && (
              // Naming the view matters here: a view that fails to compile renders
              // empty rather than unfiltered (services/views.js), so "no tickets"
              // is also what a broken view looks like. Saying which view is applied
              // is the difference between "nothing matches" and a silent dead end.
              <EmptyRow span={columns.length}>
                {activeView
                  ? `No tickets match “${activeView.name}” and these filters.`
                  : 'No tickets match these filters.'}
              </EmptyRow>
            )}

            {result.tickets.map((ticket) => (
              // Row hover is the surface→sunken step, the same one the header uses:
              // one notch off the row in both themes (1.19:1 dark, 1.10:1 light), so
              // the pointer is always locatable without a row lighting up.
              //
              // `group` exists for the muted cells below. text-ink-muted is 4.76:1 on
              // bg-surface but only 4.34:1 on bg-sunken in LIGHT mode — i.e. the
              // hovered row would drop the timestamp and the customer email just under
              // AA. Promoting them to ink-secondary on hover (6.92:1) fixes it with the
              // existing vocabulary instead of a private grey, and doubles as a hover
              // affordance. See notes: the underlying token is worth nudging.
              <tr key={ticket.id} className="group transition-colors hover:bg-sunken">
                {/* Keyed, not positional. Which columns appear and in what order is
                    the view's, so this is a map over the resolved list rather than a
                    fixed sequence of cells — and the hazard the old comment here
                    recorded (a cell at the wrong index silently rendering under the
                    wrong header at the wrong width) cannot happen at all now. */}
                {columns.map((col) => CELLS[col.key]?.(ticket) ?? null)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {page && page.total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-ink-muted">
            Showing {showingFrom.toLocaleString()}–{showingTo.toLocaleString()} of{' '}
            {page.total.toLocaleString()}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              disabled={page.offset === 0}
              onClick={() => updateFilters({ offset: Math.max(0, page.offset - PAGE_SIZE) })}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              disabled={!page.has_more}
              onClick={() => updateFilters({ offset: page.offset + PAGE_SIZE })}
            >
              Next
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
